import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useSessionStore } from '@/store/sessionStore';
import { fetchMeAccess, MeAccessUnauthorizedError, type MeAccessTenant } from '@/api/meAccess';
import { AppBar } from '@/components/AppBar';
import { useToast } from '@/components/ui';
import { ChangePasswordModal } from './profile/ChangePasswordModal';
import { LogoutAllDevicesModal } from './profile/LogoutAllDevicesModal';
import { ProfileEditView } from './profile/ProfileEditView';
import { ProfileOnboardingGate } from './profile/ProfileOnboardingGate';
import { ProfileTabs, type ProfileTabKey } from './profile/ProfileTabs';
import { ProfileInfoTab } from './profile/ProfileInfoTab';
import { ProfileAccessTab } from './profile/ProfileAccessTab';
import { ProfileSessionsTab } from './profile/ProfileSessionsTab';
import {
    endAllSessions,
    endSession,
    fetchMySessions,
    ProfileApiError,
    ProfileApiUnauthorizedError,
    type KeycloakSession,
} from './profile/profileApi';

const ONBOARDING_GATE_SEEN_KEY_PREFIX = 'portal-profile-onboarding-gate-seen:';

/**
 * EAA-1822: the onboarding gate (frame 20) is dead code today — exported from
 * `./profile` but mounted nowhere. There is no portal-api profile record (see
 * `profileFormState.ts`) so there is no real "is this account's first login"
 * flag to mount it on; the honest proxy available client-side is "has this
 * BROWSER SESSION already dismissed or finished the gate for this sub" —
 * `sessionStorage`, matching `ProfileOnboardingLauncherStrip`'s own dismiss
 * key (never `localStorage` for anything session-shaped, per
 * `authClient.ts`'s own per-tab-session rule) and the spec's "Nhắc một lần
 * mỗi lần đăng nhập" (once per login, not once ever).
 */
function hasSeenOnboardingGate(sub: string): boolean {
    try {
        return sessionStorage.getItem(ONBOARDING_GATE_SEEN_KEY_PREFIX + sub) === '1';
    } catch {
        return false;
    }
}

function markOnboardingGateSeen(sub: string): void {
    try {
        sessionStorage.setItem(ONBOARDING_GATE_SEEN_KEY_PREFIX + sub, '1');
    } catch {
        /* sessionStorage unavailable (private mode) — the gate may re-open next visit;
           harmless, never a wall (ticket rule: "Để sau" is always available anyway). */
    }
}

/**
 * `/profile` — three tabs on one route: Thông tin (frame 77), Quyền của tôi
 * (frame 78, read-only) and Phiên đăng nhập (frame 79, + the "Đăng xuất mọi
 * thiết bị" confirm of frame 82 and the "Đổi mật khẩu" dialog of frame 81).
 * Edit mode (`ProfileEditView`, frame 19 — not one of this ticket's frames)
 * stays a separate full-page swap reached from either tab's "Sửa"/"Sửa hồ sơ".
 *
 * EAA-1820 (this change): restyled from EAA-1822's single-scroll layout into
 * the tabbed frame-77/78/79 structure. The three tab bodies moved to their own
 * files (`ProfileInfoTab`/`ProfileAccessTab`/`ProfileSessionsTab`) — this
 * component now only owns route-level state (which tab, which modal is open,
 * the onboarding-gate trigger) and the data fetching every tab shares
 * (`/me/access`, `/me/sessions`). See those files' own doc comments for what
 * each frame does and does not have real data for.
 *
 * KNOWN GAP — no left icon-rail sidebar: every frame in the 77-82 set draws
 * `/profile` inside the same shell as `/console/*`. This route stays OUTSIDE
 * `ConsoleLayout` (see `App.tsx`) on purpose — `ConsoleLayout` hides its own
 * sidebar entirely for a capability-less caller (its own EAA-1814 comment),
 * which is exactly the "Nguyễn Thu Hà — Giáo viên chủ nhiệm" persona these
 * frames depict, so moving this route inside that shell would not reproduce
 * the sidebar for the persona the mockup is about. Reproducing the sidebar
 * chrome here would mean forking `ConsoleNav`'s gating logic into a second
 * copy; flagged rather than done as part of this content-focused ticket.
 */
export function ProfilePage() {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { authenticated, accessToken, claims, logout } = useAuth();
    const activeTenantId = useSessionStore((state) => state.activeTenantId);
    const setActiveTenantId = useSessionStore((state) => state.setActiveTenantId);
    const toast = useToast();
    const queryClient = useQueryClient();

    const [mode, setMode] = useState<'view' | 'edit'>('view');
    const [tab, setTab] = useState<ProfileTabKey>('info');
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [logoutAllModalOpen, setLogoutAllModalOpen] = useState(false);
    const [missingCount, setMissingCount] = useState(0);
    const [onboardingOpen, setOnboardingOpen] = useState(false);

    // EAA-1822: see `hasSeenOnboardingGate`'s doc comment for what "first
    // login" actually means here (a sessionStorage proxy, not a real flag).
    useEffect(() => {
        if (authenticated && claims?.sub && !hasSeenOnboardingGate(claims.sub)) {
            setOnboardingOpen(true);
        }
    }, [authenticated, claims?.sub]);

    function dismissOnboardingGate() {
        if (claims?.sub) markOnboardingGateSeen(claims.sub);
        setOnboardingOpen(false);
    }

    const meAccessQuery = useQuery({
        queryKey: ['me-access', accessToken, activeTenantId],
        queryFn: () => fetchMeAccess(config.apiBase, accessToken as string, activeTenantId),
        enabled: authenticated && Boolean(accessToken),
        retry: false,
    });

    const tenants: MeAccessTenant[] = meAccessQuery.data?.tenants ?? [];
    const softError =
        meAccessQuery.isError && !(meAccessQuery.error instanceof MeAccessUnauthorizedError);
    const displayName = claims?.name ?? claims?.email ?? claims?.sub ?? '';

    const activeTenant = tenants.find((tn) => tn.tenant_id === activeTenantId);
    const roleLine = activeTenant
        ? `${activeTenant.roles.join(', ') || t('profile.noRole')} · ${activeTenant.name}`
        : undefined;

    const sessionsQuery = useQuery({
        queryKey: ['me-sessions', accessToken],
        queryFn: () => fetchMySessions(config.apiBase, accessToken as string),
        enabled: authenticated && Boolean(accessToken),
        retry: false,
    });
    const sessions: KeycloakSession[] = sessionsQuery.data ?? [];
    const sessionsSoftError =
        sessionsQuery.isError && !(sessionsQuery.error instanceof ProfileApiUnauthorizedError);

    const logoutAllMutation = useMutation({
        mutationFn: () => endAllSessions(config.apiBase, accessToken as string),
        onSettled: async () => {
            // Even if ending server-side sessions failed, never strand the
            // user on this page — always finish with the LOCAL logout so
            // this device signs out regardless.
            setLogoutAllModalOpen(false);
            await logout();
        },
        onError: (error) => {
            toast.error(
                error instanceof ProfileApiError
                    ? t('profile.logoutAllErrorWithMessage', { message: error.message })
                    : t('profile.logoutAllError')
            );
        },
    });

    const endSessionMutation = useMutation({
        mutationFn: (sessionId: string) => endSession(config.apiBase, accessToken as string, sessionId),
        onSuccess: (_data, sessionId) => {
            void queryClient.invalidateQueries({ queryKey: ['me-sessions', accessToken] });
            // EAA-1822: ending the session behind THIS very request must still
            // finish with the local OIDC logout — same rule `logoutAllMutation`
            // follows — otherwise the tab keeps a token Keycloak no longer
            // recognises as belonging to a live session.
            if (claims?.sid && sessionId === claims.sid) {
                void logout();
            }
        },
        onError: () => {
            toast.error(t('profile.endSessionError'));
        },
    });

    if (mode === 'edit') {
        return (
            <div className="portal-profile-shell">
                <AppBar />
                <div className="portal-profile-topbar">
                    <span className="portal-profile-crumbs">
                        <span className="portal-profile-crumb-user">{t('profile.crumbMe')}</span>
                        <span>/</span>
                        <span className="portal-profile-crumb-current">{t('profile.crumbCurrent')}</span>
                    </span>
                    <span className="portal-profile-topbar-spacer" />
                    {missingCount > 0 && (
                        <span className="portal-profile-missing-pill">
                            {t('profile.missingCount', { count: missingCount })}
                        </span>
                    )}
                </div>
                <main className="portal-profile-content">
                    <ProfileEditView
                        displayName={displayName}
                        email={claims?.email}
                        roleLine={roleLine}
                        onMissingCountChange={setMissingCount}
                        onBack={() => setMode('view')}
                    />
                </main>
                <ProfileOnboardingGate
                    open={onboardingOpen}
                    displayName={displayName}
                    onSkip={dismissOnboardingGate}
                    onComplete={dismissOnboardingGate}
                />
            </div>
        );
    }

    return (
        <div className="portal-profile-shell">
            <AppBar />
            <div className="portal-profile-topbar">
                <span className="portal-profile-crumbs">
                    <span className="portal-profile-crumb-user">{t('profile.crumbMe')}</span>
                    <span>/</span>
                    <span className="portal-profile-crumb-current">{t('profile.crumbCurrent')}</span>
                </span>
                <span className="portal-profile-topbar-spacer" />
            </div>

            <main className="portal-profile-content">
                <div className="portal-profile-page-lede">
                    <h1 className="portal-profile-page-title">{t('profile.pageTitle')}</h1>
                    <p className="portal-profile-page-subtitle">{t('profile.pageSubtitle')}</p>
                </div>

                <ProfileTabs
                    active={tab}
                    onSelect={setTab}
                    sessionCount={sessionsQuery.isSuccess ? sessions.length : undefined}
                />

                {!authenticated || !claims ? (
                    <p className="portal-muted">{t('profile.loadingCredentials')}</p>
                ) : (
                    <>
                        {tab === 'info' && (
                            <ProfileInfoTab
                                claims={claims}
                                displayName={displayName}
                                roleLine={roleLine}
                                onEditProfile={() => setMode('edit')}
                                onOpenPasswordModal={() => setPasswordModalOpen(true)}
                            />
                        )}
                        {tab === 'access' && (
                            <ProfileAccessTab
                                isLoading={meAccessQuery.isLoading}
                                softError={softError}
                                isSuccess={meAccessQuery.isSuccess}
                                tenants={tenants}
                                activeTenantId={activeTenantId}
                                onSwitchTenant={setActiveTenantId}
                            />
                        )}
                        {tab === 'sessions' && (
                            <ProfileSessionsTab
                                isLoading={sessionsQuery.isLoading}
                                softError={sessionsSoftError}
                                isSuccess={sessionsQuery.isSuccess}
                                sessions={sessions}
                                currentSid={claims.sid}
                                endingSessionId={
                                    endSessionMutation.isPending
                                        ? (endSessionMutation.variables as string | undefined)
                                        : undefined
                                }
                                onEndSession={(sessionId) => endSessionMutation.mutate(sessionId)}
                                onOpenLogoutAllModal={() => setLogoutAllModalOpen(true)}
                                onOpenPasswordModal={() => setPasswordModalOpen(true)}
                            />
                        )}
                    </>
                )}
            </main>

            <ChangePasswordModal open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} />
            <LogoutAllDevicesModal
                open={logoutAllModalOpen}
                sessionCount={sessions.length}
                pending={logoutAllMutation.isPending}
                onClose={() => setLogoutAllModalOpen(false)}
                onConfirm={() => logoutAllMutation.mutate()}
            />
            <ProfileOnboardingGate
                open={onboardingOpen}
                displayName={displayName}
                onSkip={dismissOnboardingGate}
                onComplete={dismissOnboardingGate}
            />
        </div>
    );
}

export default ProfilePage;
